import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import {
  Zap, Plus, Layout, Clock, Settings, ArrowRight,
  CheckCircle2, Folder, Terminal, Loader2, ChevronLeft,
  FileCode2, LogOut, User, Image as ImageIcon, PenTool, CircleDashed
} from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { supabase } from './supabaseClient';

const CORE_API = "https://lu5zfciin5mk34fowavmhz7dt40pkhhg.lambda-url.us-east-1.on.aws";

function cn(...inputs) { return twMerge(clsx(inputs)); }

// --- ADVANCED TAG PARSER & CLEANER ---
function extractFileTags(markdownText) {
  if (!markdownText) return { tags: [], cleanText: '' };
  
  const tagRegex = /([+~])@([\w/.-]+)(?::([\w]+))?/g;
  const tags = [];
  let match;

  while ((match = tagRegex.exec(markdownText)) !== null) {
    const action = match[1]; 
    const path = match[2];
    const component = match[3];

    let type = 'code';
    if (path.match(/\.(png|jpg|jpeg|svg|gif)$/i)) type = 'image';
    if (path.match(/\.(fig|sketch|psd)$/i)) type = 'design';

    tags.push({ action, path, component, type });
  }
  
  let cleanText = markdownText
    .replace(/Targets?:?/gi, '') 
    .replace(/[+~]@[\w/.-]+(?::[\w]+)?/g, '') 
    .replace(/^[(),\s]+|[(),\s]+$/g, '') 
    .replace(/\n{3,}/g, '\n\n') 
    .trim();

  return { tags, cleanText };
}

// --- RESPONSIVE MARKDOWN STYLING ---
const markdownComponents = {
  h1: ({node, ...props}) => <h1 className="text-xl md:text-2xl font-black mt-6 md:mt-8 mb-3 md:mb-4 text-white tracking-tight" {...props} />,
  h2: ({node, ...props}) => <h2 className="text-lg md:text-xl font-bold mt-6 md:mt-8 mb-3 md:mb-4 text-white" {...props} />,
  h3: ({node, ...props}) => <h3 className="text-base md:text-lg font-bold mt-5 md:mt-6 mb-2 text-zinc-200 uppercase tracking-wide" {...props} />,
  p: ({node, ...props}) => <p className="text-sm md:text-base text-zinc-400 leading-relaxed mb-4 break-words" {...props} />,
  ul: ({node, ...props}) => <ul className="list-disc list-outside ml-5 mb-4 text-zinc-400 space-y-1.5 md:space-y-2 text-sm md:text-base" {...props} />,
  ol: ({node, ...props}) => <ol className="list-decimal list-outside ml-5 mb-4 text-zinc-400 space-y-1.5 md:space-y-2 text-sm md:text-base" {...props} />,
  li: ({node, ...props}) => <li className="pl-1 break-words" {...props} />,
  code: ({node, inline, ...props}) => inline 
    ? <code className="bg-zinc-800/80 text-purple-400 px-1.5 py-0.5 rounded text-xs md:text-sm font-mono border border-zinc-700/50 break-words" {...props} />
    : <div className="bg-[#050508] border border-zinc-800 rounded-lg p-3 md:p-4 mb-4 w-full max-w-[calc(100vw-3rem)] md:max-w-none overflow-x-auto"><code className="text-zinc-300 text-xs md:text-sm font-mono" {...props} /></div>,
  strong: ({node, ...props}) => <strong className="font-bold text-zinc-200" {...props} />,
  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-purple-500 pl-4 italic text-zinc-500 my-4 text-sm md:text-base" {...props} />
};

export default function Dashboard() {
  const [view, setView] = useState('dashboard');
  const [activeProject, setActiveProject] = useState(null);
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setAccessToken(session.access_token);
        setUser(session.user);
      } else {
        navigate('/'); 
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => { checkSession(); }, []);

  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      setUser(session.user);
      setAccessToken(session.access_token);
      fetchProjects(session.user.id);
      await handleUrlNavigation();
    } else {
      setLoading(false);
      navigate('/');
    }
  };

  const handleUrlNavigation = async () => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('projectId');
    const viewParam = params.get('view');

    if (projectId) {
      setLoading(true);
      const { data, error } = await supabase.from('leads').select('*').eq('id', projectId).single();
      if (!error && data) { setActiveProject(data); setView('workspace'); }
      setLoading(false);
    } else if (viewParam === 'create') { 
      setView('create'); 
      setLoading(false);
    } else { 
      setView('dashboard'); 
      setLoading(false); 
    }
  };

  const fetchProjects = async (userId) => {
    const { data, error } = await supabase.from('leads').select('*').eq('user_id', userId).order('created_at', { ascending: false });
    if (!error && data) setProjects(data);
    if (!window.location.search.includes('projectId')) setLoading(false);
  };

  const handleOpenProject = (project) => {
    setActiveProject(project);
    setView('workspace');
    const url = new URL(window.location.href);
    url.searchParams.set('projectId', project.id);
    window.history.pushState({}, '', url);
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
    setActiveProject(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('projectId');
    url.searchParams.delete('view');
    window.history.pushState({}, '', url);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#050508] text-zinc-50 font-sans selection:bg-purple-500/30">
      <nav className="border-b border-zinc-800/80 bg-[#0A0A0F]/80 backdrop-blur-md sticky top-0 z-50 h-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2 font-black text-xl tracking-tighter italic cursor-pointer" onClick={handleBackToDashboard}>
            <Zap className="text-purple-500 fill-current" size={20} />
            THRUST
          </div>
          
          <div className="flex items-center gap-3 md:gap-4">
            {activeProject && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-800/50 border border-zinc-700 text-xs font-bold text-zinc-400 max-w-[200px] truncate">
                <Folder size={12} className="shrink-0" />
                <span className="truncate">{activeProject.name}</span>
              </div>
            )}
            
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-tr from-purple-600 to-blue-600 ring-2 ring-zinc-950 hover:ring-purple-500 transition-all text-sm font-bold text-white shadow-lg shrink-0">
                {user?.email ? user.email.charAt(0).toUpperCase() : <User size={14} />}
              </button>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} transition={{ duration: 0.15 }} className="absolute right-0 mt-3 w-56 md:w-64 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden z-50 origin-top-right">
                    <div className="p-4 border-b border-zinc-800 bg-zinc-900/50">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Signed in as</p>
                      <p className="text-xs md:text-sm font-medium text-white truncate">{user?.email}</p>
                    </div>
                    <div className="p-2">
                      <button onClick={() => setDropdownOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"><Settings size={16} />Account Settings</button>
                      <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors mt-1"><LogOut size={16} />Logout</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <AnimatePresence mode="wait">
          {view === 'dashboard' && (
            <motion.div key="dashboard" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6 md:space-y-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-1 md:mb-2">Your Projects</h1>
                  <p className="text-sm md:text-base text-zinc-500 font-medium">Select a Lead to enter flow state.</p>
                </div>
                <button onClick={() => setView('create')} className="w-full sm:w-auto bg-purple-600 text-white hover:bg-purple-500 px-4 py-2.5 md:py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-sm">
                  <Plus size={16} /> New Lead
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="animate-spin text-zinc-700" /></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {projects.map((p) => (
                    <div key={p.id} onClick={() => handleOpenProject(p)} className="group p-5 md:p-6 rounded-2xl border border-zinc-800/80 bg-[#0F0F16] hover:bg-zinc-900 hover:border-zinc-700 transition-all cursor-pointer relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-5 md:p-6 opacity-0 group-hover:opacity-100 transition-opacity"><ArrowRight className="text-zinc-500 group-hover:text-purple-500 -rotate-45 group-hover:rotate-0 transition-transform duration-300" /></div>
                      <div className="mb-4">
                        <div className="h-10 w-10 rounded-lg bg-zinc-800/50 flex items-center justify-center mb-4 group-hover:bg-purple-500/10 group-hover:text-purple-400 transition-colors text-zinc-500"><Terminal size={20} /></div>
                        <h3 className="font-bold text-lg mb-1 truncate">{p.name}</h3>
                        <p className="text-sm text-zinc-500 line-clamp-2">{p.description}</p>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-600"><div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{p.status}</div>
                    </div>
                  ))}
                  <button onClick={() => setView('create')} className="p-5 md:p-6 rounded-2xl border border-dashed border-zinc-800 bg-transparent hover:bg-zinc-900/50 hover:border-zinc-700 transition-all flex flex-col items-center justify-center gap-4 text-zinc-600 hover:text-zinc-400 h-[200px] md:h-[220px]">
                    <Plus size={32} /><span className="font-bold text-sm">Initialize New Project</span>
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {view === 'create' && <CreateProjectView userId={user?.id} accessToken={accessToken} onBack={handleBackToDashboard} onCreated={(newProject) => { setProjects([newProject, ...projects]); handleOpenProject(newProject); }} />}
          {view === 'workspace' && activeProject && <WorkspaceView project={activeProject} onBack={handleBackToDashboard} />}
        </AnimatePresence>
      </main>
    </div>
  );
}

// ==========================================
// SUB-COMPONENT: CREATE PROJECT VIEW
// ==========================================
function CreateProjectView({ onBack, onCreated, userId, accessToken}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Calculate user's exact timezone offset in hours (e.g., -5 for EST, 5.5 for IST)
 const offsetHours = new Date().getTimezoneOffset() / -60;

      const response = await fetch(`${CORE_API}/api/projects/init`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` }, 
        body: JSON.stringify({ 
            userId: userId, // Restored actual dynamic userId
            name, 
            description: desc, 
            localPath: "",
            timezoneOffset: offsetHours // Send to Core
        }) 
      });
      const result = await response.json();
      if (result.success) {
        const { data } = await supabase.from('leads').select('*').eq('id', result.leadId).single();
        if (data) onCreated(data);
      }
    } catch (error) { alert("Failed to initialize project."); } 
    finally { setIsSubmitting(false); }
  };

  /* const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${CORE_API}/init-project`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` }, body: JSON.stringify({ userId, name, description: desc, localPath: "" }) });
      const result = await response.json();
      if (result.success) {
        const { data } = await supabase.from('leads').select('*').eq('id', result.leadId).single();
        if (data) onCreated(data);
      }
    } catch (error) { alert("Failed to initialize project."); } 
    finally { setIsSubmitting(false); }
  }; */

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="max-w-2xl mx-auto">
      <button onClick={onBack} className="mb-6 text-zinc-500 hover:text-white flex items-center gap-2 text-sm font-bold transition-colors"><ChevronLeft size={16} /> Back to Dashboard</button>
      <div className="bg-[#0F0F16] border border-zinc-800 rounded-2xl p-5 md:p-8 shadow-2xl">
        <div className="mb-6 md:mb-8"><h2 className="text-xl md:text-2xl font-black mb-2">Initialize New Lead</h2><p className="text-zinc-500 text-xs md:text-sm">The AI Architect will analyze your request and generate requirements.</p></div>
        <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
          <div><label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Project Name</label><input required value={name} onChange={e => setName(e.target.value)} className="w-full bg-[#050508] border border-zinc-800 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500 transition-colors font-bold text-sm md:text-base" /></div>
          <div><label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Description / Aim</label><textarea required value={desc} onChange={e => setDesc(e.target.value)} className="w-full h-32 bg-[#050508] border border-zinc-800 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500 transition-colors resize-none leading-relaxed text-sm md:text-base" /></div>
          <div className="pt-2 md:pt-4"><button disabled={isSubmitting} className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3.5 md:py-4 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-3 text-sm md:text-base">{isSubmitting ? <Loader2 className="animate-spin" /> : <Zap size={18} fill="currentColor" />}{isSubmitting ? "Architecting in background..." : "Initialize Thrust"}</button></div>
        </form>
      </div>
    </motion.div>
  );
}

// ==========================================
// SUB-COMPONENT: WORKSPACE VIEW
// ==========================================
function WorkspaceView({ project, onBack }) {
  const [tab, setTab] = useState('momentum');
  const [thrust, setThrust] = useState(null);
  const [timeline, setTimeline] = useState([]);

  const fetchWorkspaceData = async () => {
    const { data: thrusts } = await supabase.from('thrusts').select('*, tasks:thrust_tasks(*)').eq('lead_id', project.id).eq('status', 'active').order('created_at', { ascending: false }).limit(1).single();
    if (thrusts) setThrust(thrusts);
    const { data: events } = await supabase.from('timeline_events').select('*').eq('lead_id', project.id).order('created_at', { ascending: false });
    if (events) setTimeline(events);
  };

  useEffect(() => {
    fetchWorkspaceData();
    let interval = setInterval(() => { fetchWorkspaceData(); }, 3000);
    return () => clearInterval(interval);
  }, [project.id]);

  const toggleTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'done' ? 'pending' : 'done';
    if (thrust) {
      const updatedTasks = thrust.tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t);
      setThrust({ ...thrust, tasks: updatedTasks });
    }
    await supabase.from('thrust_tasks').update({ status: newStatus }).eq('id', taskId);
  };

  const { tags: fileTags, cleanText: cleanMarkdown } = extractFileTags(thrust?.markdown_content);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6 mb-6 md:mb-8 border-b border-zinc-800 pb-4 md:pb-6">
        <div>
          <button onClick={onBack} className="text-zinc-500 text-xs font-bold uppercase tracking-widest hover:text-white mb-2 flex items-center gap-1"><ChevronLeft size={12} /> Dashboard</button>
          <h1 className="text-2xl md:text-3xl font-black truncate">{project.name}</h1>
        </div>
        {/* TABS (Scrollable on small screens if needed) */}
        <div className="flex bg-[#0F0F16] p-1 rounded-xl border border-zinc-800 overflow-x-auto hide-scrollbar">
          <TabButton active={tab === 'momentum'} onClick={() => setTab('momentum')} icon={Zap} label="Briefing" />
          <TabButton active={tab === 'timeline'} onClick={() => setTab('timeline')} icon={Clock} label="Timeline" />
          <TabButton active={tab === 'settings'} onClick={() => setTab('settings')} icon={Settings} label="Settings" />
        </div>
      </div>

      <div className="min-h-[500px]">
        {tab === 'momentum' && (
          <div className="grid lg:grid-cols-[1fr_350px] gap-6 md:gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="bg-[#0F0F16] border border-zinc-800/80 rounded-2xl p-5 md:p-8 shadow-sm flex flex-col max-w-full overflow-hidden">
              <div className="flex items-center gap-3 mb-5 md:mb-6 border-b border-zinc-800/50 pb-4">
                <div className="p-2 bg-purple-500/10 text-purple-400 rounded-lg"><Layout size={18} className="md:w-5 md:h-5" /></div>
                <span className="font-bold text-xs md:text-sm text-zinc-400">Current Thrust Directive</span>
              </div>

              {thrust ? (
                <>
                  {fileTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6 md:mb-8 p-3 md:p-4 bg-[#050508] border border-zinc-800/80 rounded-xl">
                      <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-zinc-500 w-full mb-1">Target Files</span>
                      {fileTags.map((tag, idx) => {
                        const Icon = tag.type === 'image' ? ImageIcon : tag.type === 'design' ? PenTool : FileCode2;
                        const isCreate = tag.action === '+';
                        return (
                          <div key={idx} className={cn("flex items-center gap-1.5 px-2.5 py-1.5 border rounded-md text-[10px] md:text-xs font-mono font-medium truncate max-w-full", isCreate ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" : "bg-blue-500/10 border-blue-500/20 text-blue-400")}>
                            <Icon size={12} className="shrink-0" />
                            <span className="truncate">{isCreate ? 'Create: ' : 'Modify: '} {tag.path}</span>
                            {tag.component && <span className="text-zinc-500 ml-1 shrink-0">({tag.component})</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="w-full overflow-hidden">
                    <ReactMarkdown components={markdownComponents}>
                      {cleanMarkdown}
                    </ReactMarkdown>
                  </div>
                </>
              ) : (
                <div className="text-center py-20 text-zinc-500 m-auto"><Loader2 className="animate-spin mx-auto mb-4" size={32} /><p className="font-medium animate-pulse text-sm md:text-base">AI Architect is building your Thrust...</p></div>
              )}
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.1 }} className="space-y-6">
              <div className="bg-[#0F0F16] border border-zinc-800/80 rounded-2xl p-5 md:p-6">
                <h3 className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-3 md:mb-4">AI Mission Checklist</h3>
                <p className="text-[10px] md:text-xs text-zinc-500 mb-4 border-b border-zinc-800 pb-4">The AI Analyst automatically checks these off as you code.</p>
                <div className="space-y-2 md:space-y-3">
                  {thrust?.tasks?.map(task => (
                    <div key={task.id} className="flex items-start gap-3 p-3 rounded-xl bg-[#050508] border border-zinc-800/50 transition-colors group overflow-hidden">
                      <div className={cn("mt-0.5 h-4 w-4 md:h-5 md:w-5 rounded-full border flex items-center justify-center transition-colors shrink-0", task.status === 'done' ? "bg-purple-600 border-purple-600" : "border-zinc-700 bg-zinc-900")}>
                        {task.status === 'done' ? <CheckCircle2 size={12} className="text-white md:w-3.5 md:h-3.5" /> : <CircleDashed size={12} className="text-zinc-600 md:w-3.5 md:h-3.5" />}
                      </div>
                      <span className={cn("text-xs md:text-sm font-medium leading-relaxed break-words", task.status === 'done' ? "text-zinc-500 line-through" : "text-zinc-200")}>{task.title}</span>
                    </div>
                  ))}
                  {(!thrust?.tasks || thrust.tasks.length === 0) && <p className="text-xs text-zinc-600 italic">{thrust ? "All tasks completed." : "Waiting for AI..."}</p>}
                </div>
              </div>

              <div className="bg-[#0F0F16] border border-zinc-800/80 rounded-2xl p-5 md:p-6">
                <h3 className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-4">Project Context</h3>
                <div className="space-y-3 md:space-y-4">
                  <ContextItem label="PRD Status" value={thrust ? "Locked" : "Drafting..."} color={thrust ? "text-emerald-500" : "text-yellow-500 animate-pulse"} />
                  <ContextItem label="Core API" value="Connected" color="text-emerald-500" />
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {tab === 'timeline' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto px-2 md:px-0">
            <div className="space-y-6 md:space-y-8 relative before:absolute before:left-3 md:before:left-4 before:top-0 before:bottom-0 before:w-0.5 before:bg-zinc-800">
              {timeline.map((event) => (
                <div key={event.id} className="relative pl-10 md:pl-12">
                  <div className={cn("absolute left-[0.35rem] md:left-2 top-1.5 h-3 w-3 md:h-4 md:w-4 rounded-full border-2 border-zinc-950 -translate-x-1/2", event.type === 'feature' ? "bg-purple-500" : event.type === 'error' ? "bg-red-500" : "bg-zinc-600")} />
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 md:p-6">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                        <span className={cn("text-[9px] md:text-[10px] font-bold px-2 py-0.5 rounded uppercase", event.type === 'feature' ? "bg-purple-500/10 text-purple-400" : "bg-zinc-800 text-zinc-400")}>{event.type}</span>
                        <span className="text-[10px] md:text-xs text-zinc-500 font-mono">{new Date(event.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <h4 className="font-bold text-base md:text-lg text-white mb-1.5 md:mb-2">{event.title}</h4>
                    <p className="text-zinc-400 text-xs md:text-sm leading-relaxed">{event.description}</p>
                  </div>
                </div>
              ))}
              {timeline.length === 0 && <div className="text-center py-20 text-zinc-500 pl-12 text-sm">No timeline events recorded yet.</div>}
            </div>
          </motion.div>
        )}

        {tab === 'settings' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl px-2 md:px-0">
            <h3 className="text-lg md:text-xl font-bold mb-4 md:mb-6">Settings</h3>
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 md:p-6 mb-6">
              <h4 className="font-bold text-sm md:text-base mb-2 md:mb-4">Danger Zone</h4>
              <p className="text-xs md:text-sm text-zinc-500 mb-4 md:mb-6">Once you archive a project, the AI context is frozen.</p>
              <button className="w-full sm:w-auto border border-red-500/20 bg-red-500/5 text-red-500 px-4 py-2.5 md:py-2 rounded-lg text-xs md:text-sm font-bold hover:bg-red-500/10 transition-colors">
                Archive Project
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button onClick={onClick} className={cn("flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition-all whitespace-nowrap", active ? "bg-zinc-800 text-white shadow-sm" : "text-zinc-500 hover:text-zinc-300")}>
    <Icon size={14} className="md:w-4 md:h-4" />{label}
  </button>
);

const ContextItem = ({ label, value, color }) => (
  <div className="flex justify-between items-center text-xs md:text-sm"><span className="text-zinc-500 font-medium">{label}</span><span className={cn("font-bold text-right", color)}>{value}</span></div>
);
